import MediaPickerModalButton from '@/Components/inputs/MediaPicker';
import { ImagePreview } from '@/Components/preview/ImagePreview';
import { MimeTypeEnum } from '@/graphql/types';
import { Components, type LevaInputProps, createPlugin, useInputContext } from 'leva/plugin';
import { useMemo } from 'react';

const { Label, Row } = Components;

type ValueType = { id: string | null | undefined; mediaItemUrl: string | null | undefined };

type ConfigInputType = {
  input: {
    value: ValueType;
    type: 'image' | 'video';
  };
};

type InternalSettingsType = Omit<ConfigInputType['input'], 'value'>;

type InteralPropsType = LevaInputProps<ValueType, InternalSettingsType>;

const ImageArray = [MimeTypeEnum.ImageGif, MimeTypeEnum.ImageJpeg, MimeTypeEnum.ImagePng, MimeTypeEnum.ImageWebp];
const VideoArray = [MimeTypeEnum.VideoMp4, MimeTypeEnum.VideoWebm, MimeTypeEnum.VideoOgg];

const sanitizeValue = (value: ValueType, settings: InternalSettingsType): ValueType => {
  return value;
};

const plugin = createPlugin<ConfigInputType, ValueType, InternalSettingsType>({
  sanitize: (value: ValueType, settings, prevValue, path, store) => {
    return sanitizeValue(value, settings);
  },
  format: (value: ValueType, settings) => {
    return {
      value: sanitizeValue(value, settings),
    };
  },
  normalize: (input: ConfigInputType, path, data) => {
    const { value, ...settings } = input.input;

    return {
      value: value ?? [],
      settings: settings,
    };
  },
  component: Component,
});

function Component() {
  const { label, value, onUpdate, settings } = useInputContext<InteralPropsType>();

  const mimeTypes = useMemo(() => {
    if (settings?.type === 'image') {
      return ImageArray;
    }
    if (settings?.type === 'video') {
      return VideoArray;
    }
    return [];
  }, [settings]);

  return (
    <Row input>
      <Label>{label}</Label>
      <div>
        {settings?.type === 'image' && !value?.id?.length && (
          <MediaPickerModalButton
            mimeTypes={mimeTypes}
            onFileSelect={(file) => {
              onUpdate({
                id: file?.id,
                mediaItemUrl: file?.mediaItemUrl,
              } as ValueType);
            }}
          />
        )}
        {settings?.type === 'image' && !!value?.id?.length && (
          <ImagePreview
            node={{ ...value }}
            onClear={() => onUpdate({ id: null, mediaItemUrl: null } as ValueType)}
            style={{
              height: '50px',
              backgroundColor: '#f0f1f3',
            }}
            slotProps={{
              img: {
                objectFit: 'contain',
              },
            }}
          />
        )}
      </div>
    </Row>
  );
}

export default plugin;
